import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, type ButtonProps } from './Button';
import { cx } from './cx';

export type DropdownItem =
  | {
      type?: 'item';
      label: string;
      onSelect: () => void;
      icon?: ReactNode;
      danger?: boolean;
      disabled?: boolean;
    }
  | { type: 'separator' };

export interface DropdownMenuProps {
  /** Visible trigger text (or aria-label when `triggerIcon` only). */
  label: string;
  items: readonly DropdownItem[];
  align?: 'start' | 'end';
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  /** Icon-only trigger: `label` becomes the accessible name. */
  triggerIcon?: ReactNode;
  /** Render the trigger for dark chrome (navbar). */
  inverse?: boolean;
  className?: string;
}

/**
 * Menu button (WAI-ARIA APG "Menu Button"): Enter/Space/↓ open on the first item, ↑ on the last;
 * ↑/↓ move with wrap, Home/End, type-ahead; Esc closes and returns focus to the trigger; Tab and
 * outside clicks close. Items are role="menuitem" with roving focus.
 */
export function DropdownMenu({
  label,
  items,
  align = 'start',
  variant = 'secondary',
  size = 'md',
  triggerIcon,
  inverse,
  className,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, '');
  const enabled = items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => it.type !== 'separator' && !it.disabled)
    .map(({ i }) => i);

  const openAt = (pos: 'first' | 'last') => {
    setActive(pos === 'first' ? (enabled[0] ?? 0) : (enabled.at(-1) ?? 0));
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.focus();
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const move = (delta: number) => {
    const pos = enabled.indexOf(active);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    if (next !== undefined) setActive(next);
  };

  const onTriggerKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAt('first');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openAt('last');
    }
  };

  const onMenuKey = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActive(enabled[0] ?? 0);
        break;
      case 'End':
        e.preventDefault();
        setActive(enabled.at(-1) ?? 0);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const ch = e.key.toLowerCase();
          const order = [...enabled.slice(enabled.indexOf(active) + 1), ...enabled];
          const hit = order.find((i) => {
            const it = items[i];
            return it && it.type !== 'separator' && it.label.toLowerCase().startsWith(ch);
          });
          if (hit !== undefined) setActive(hit);
        }
    }
  };

  const select = (i: number) => {
    const it = items[i];
    if (!it || it.type === 'separator' || it.disabled) return;
    close();
    it.onSelect();
  };

  return (
    <div ref={rootRef} className={cx('relative inline-block', inverse && 'on-dark', className)}>
      <Button
        ref={triggerRef}
        id={`${id}-trigger`}
        variant={inverse ? 'inverse' : variant}
        size={size}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        aria-label={triggerIcon ? label : undefined}
        onClick={() => (open ? close(false) : openAt('first'))}
        onKeyDown={onTriggerKey}
        iconEnd={triggerIcon ? undefined : <ChevronDown className="size-4" />}
      >
        {triggerIcon ?? label}
      </Button>
      {open && (
        <div
          ref={menuRef}
          id={`${id}-menu`}
          role="menu"
          aria-labelledby={`${id}-trigger`}
          onKeyDown={onMenuKey}
          className={cx(
            'absolute top-full z-(--z-overlay) mt-1 min-w-44 border border-border-widget bg-surface py-1 shadow-dropdown',
            align === 'end' ? 'end-0' : 'start-0',
          )}
        >
          {items.map((it, i) =>
            it.type === 'separator' ? (
              <div key={`sep-${i}`} role="separator" className="my-1 border-t border-border" />
            ) : (
              <div
                key={it.label}
                role="menuitem"
                tabIndex={i === active ? 0 : -1}
                data-index={i}
                aria-disabled={it.disabled ? true : undefined}
                onClick={() => select(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    select(i);
                  }
                }}
                onPointerMove={() => !it.disabled && setActive(i)}
                className={cx(
                  'flex min-h-8 cursor-pointer items-center gap-2 px-3 text-sm outline-none',
                  it.danger ? 'text-danger-ink' : 'text-ink',
                  'focus:bg-primary focus:text-ink-inverse',
                  it.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {it.icon && (
                  <span aria-hidden="true" className="inline-flex size-4">
                    {it.icon}
                  </span>
                )}
                {it.label}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
